'use client';
import { useForm, UseFormReturnType } from '@mantine/form';
import {
  useCompleteSupplierIntakeMutation,
  useGetPresignedUrlMutation,
  useUpdateOrgMutation,
  useUpdateSupplierStatusFlagsMutation,
} from '@scaleup/frontend/api';
import { UseSupplierStatusFlags, UseUserPreference } from '@scaleup/frontend/hooks';
import { dayjs, FORM_VALIDATIONS } from '@scaleup/frontend/utils';
import { Certification, NAICS, Org, Supplier } from '@scaleup/models';
import { set } from 'lodash';
import { SupplierStatusFlags } from '../../../../packages/models/src/supplier';

import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';

type IntakeStep =
  | 'init'
  | 'details'
  | 'demographics'
  | 'industry'
  | 'structure'
  | 'naics'
  | 'review'
  | 'final';

type SupplierIntakeContextType = {
  supplier: Supplier;
  lastStep: IntakeStep;
  setLastStep: (s: IntakeStep) => void;
  currentStep: IntakeStep;
  setCurrentStep: (s: IntakeStep) => void;
  show: boolean;
  setShow: (b: boolean) => void;
  detailsForm: UseFormReturnType<any>;
  industryForm: UseFormReturnType<any>;
  structureForm: UseFormReturnType<any>;
  demographicCertifications: Certification[];
  setDemographicCertifications: (a: Certification[]) => void;
  naics: NAICS[];
  setNAICS: (naics: NAICS[]) => void;
  primaryNAICS: NAICS | undefined;
  setPrimaryNAICS: (naics: NAICS | undefined) => void;
  updateSupplier: (supplier: Supplier) => Promise<Supplier>;
  completeSupplierIntake: () => Promise<void>;
} & UseSupplierStatusFlags;

const SupplierIntakeContext = createContext<SupplierIntakeContextType>(
  {} as SupplierIntakeContextType
);

interface SupplierIntakeContextProviderProps extends PropsWithChildren {
  supplier: Supplier;
  prefs: UseSupplierStatusFlags;
}

export const useIntakeUpload = () => {
  const { supplier } = useSupplierIntakeContext();
  const [getPresignedUrl, { isLoading }] = useGetPresignedUrlMutation();

  const intakeFileUpload = async (
    file: File,
    onComplete: (s?: string) => void
  ) => {
    const presignedUrl = await getPresignedUrl({
      contentType: file.type,
      fileName: file.name,
    });

    if (presignedUrl.data?.presignedUrl) {
      await fetch(presignedUrl.data.presignedUrl, {
        method: 'PUT',
        body: file,
      });
      onComplete(`${supplier.orgId}/${file.name}`);
    }
  };

  return { isLoading, intakeFileUpload };
};

export const SupplierIntakeContextProvider: FC<
  SupplierIntakeContextProviderProps
> = ({ children, supplier, prefs }) => {
  const [currentStep, setCurrentStep] = useState<IntakeStep>('init');
  const [lastStep, setLastStep] = useState<IntakeStep>('init');
  const [show, setShow] = useState(true);
  const [demographicCertifications, setDemographicCertifications] = useState<
    Certification[]
  >((supplier.supplierData.demographicCertifications || []).concat(supplier.supplierData.industryCertifications || []));
  const [naics, setNAICS] = useState<NAICS[]>(
    supplier.supplierData.naics || []
  );
  const [primaryNAICS, setPrimaryNAICS] = useState<NAICS | undefined>(
    supplier.supplierData.primaryNAICS
  );
  const [updateOrg, { isLoading }] = useUpdateOrgMutation();

  const [completeSupplierIntakeMutation, { isLoading: isCompleting }] =
    useCompleteSupplierIntakeMutation();

  const [ updateSupplierStatusFlagsMutation, {isLoading: updatingFlag}] = useUpdateSupplierStatusFlagsMutation();
  const supplierData = supplier.supplierData;

  const findYearlyTotalRevenue = (year: number) => {
    const value = supplierData.yearlyTotalRevenue?.find(
      (revenue) => revenue.year === year
    );
    if (value) {
      return value.revenue;
    }
    return undefined;
  }
  const detailsForm = useForm({
    mode: 'controlled',
    validateInputOnChange: true,
    initialValues: {
      //  section 1
      address1: supplier.address1,
      address2: supplier.address2,
      city: supplier.city,
      state: supplier.state,
      zipCode: Number(supplier.zipCode),
      contactEmail: supplier.contactEmail,
      contactPhone: supplier.contactPhone,
      website: supplier.website,
      // zipCode: supplier.zipCode,

      //  section 2
      industry: supplierData.industry,
      primaryIndustrySector: supplierData.primaryIndustrySector,
      description: supplierData.description,
      productsAndServices: supplierData.productsAndServices || [],
      establishmentYear: supplierData.establishmentYear,
      geographiesServed: supplierData.geographiesServed || [],
      multipleSites: String(supplierData.multipleSites || false),
      primaryBusinessActivity: supplierData.primaryBusinessActivity,
      legalOrganizationStatus: supplierData.legalOrganizationStatus,
      primaryShareholder: supplierData.primaryShareholder,
      demographicDesignations: supplierData.demographicDesignations || [],

      // section 3
      mainContactFirstName: supplierData.mainContact?.firstName ?? '',
      mainContactLastName: supplierData.mainContact?.lastName ?? '',
      mainContactTitle: supplierData.mainContact?.title ?? '',
      mainContactEmail: supplierData.mainContact?.email ?? '',
      mainContactPhone: supplierData.mainContact?.phone ?? '',
      
      keyContact1FirstName: supplierData.keyContact1?.firstName ?? '',
      keyContact1LastName: supplierData.keyContact1?.lastName ?? '',
      keyContact1Title: supplierData.keyContact1?.title ?? '',
      keyContact1Email: supplierData.keyContact1?.email ?? '',
      keyContact1Phone: supplierData.keyContact1?.phone ?? '',

      keyContact2FirstName: supplierData.keyContact2?.firstName ?? '',
      keyContact2LastName: supplierData.keyContact2?.lastName ?? '',
      keyContact2Title: supplierData.keyContact2?.title ?? '',
      keyContact2Email: supplierData.keyContact2?.email ?? '',
      keyContact2Phone: supplierData.keyContact2?.phone ?? '',
    },
    validate: {
      // section 1
      address1: FORM_VALIDATIONS.address1,
      address2: FORM_VALIDATIONS.address2,
      city: FORM_VALIDATIONS.city,
      state: FORM_VALIDATIONS.state,
      zipCode: FORM_VALIDATIONS.zipCode,
      contactEmail: (value: string | undefined) => FORM_VALIDATIONS.email(true)(value ?? ''),
      contactPhone: (value: string | undefined) => FORM_VALIDATIONS.phone(false)(value ?? ''),
      website: FORM_VALIDATIONS.url,

      // section 2
      industry: FORM_VALIDATIONS.basicText('Industry', 4),
      primaryIndustrySector: FORM_VALIDATIONS.basicText(
        'Primary Industry Sector',
        4
      ),
      description: FORM_VALIDATIONS.words('description', 5),
      productsAndServices: FORM_VALIDATIONS.list('product or service', true),
      establishmentYear: FORM_VALIDATIONS.year,

      geographiesServed: FORM_VALIDATIONS.list('location', true),
      multipleSites: FORM_VALIDATIONS.noop,
      primaryBusinessActivity: FORM_VALIDATIONS.basicText(
        'Primary Business Activity',
        3
      ),
      legalOrganizationStatus: FORM_VALIDATIONS.basicText(
        'Legal Organization Status',
        3
      ),
      primaryShareholder: FORM_VALIDATIONS.basicText('Primary Shareholder', 5),

      // section 3
      mainContactFirstName: FORM_VALIDATIONS.name('First'),
      mainContactLastName: FORM_VALIDATIONS.name('Last'),
      mainContactTitle: FORM_VALIDATIONS.basicText('Title', 3),
      mainContactEmail: FORM_VALIDATIONS.email(),
      mainContactPhone: FORM_VALIDATIONS.phone(false),

      keyContact1FirstName: FORM_VALIDATIONS.name('First', false),
      keyContact1LastName: FORM_VALIDATIONS.name('Last', false),
      keyContact1Title: FORM_VALIDATIONS.basicText('Title', 3, false),
      keyContact1Email: FORM_VALIDATIONS.email(false),
      keyContact1Phone: FORM_VALIDATIONS.phone(false),

      keyContact2FirstName: FORM_VALIDATIONS.name('First', false),
      keyContact2LastName: FORM_VALIDATIONS.name('Last', false),
      keyContact2Title: FORM_VALIDATIONS.basicText('Title', 3, false),
      keyContact2Email: FORM_VALIDATIONS.email(false),
      keyContact2Phone: FORM_VALIDATIONS.phone(false),
    },
  });

  const structureForm = useForm({
    mode: 'controlled',
    validateInputOnChange: true,
    initialValues: {
      // section 1
      ownershipQuestion: supplierData.ownershipQuestion,
      numberOfEmployees: supplierData.numberOfEmployees,
      numberOfContractors: supplierData.numberOfContractors,

      boardOfAdvisors: String(supplierData.boardOfAdvisors || false),
      bOAExpectations: supplierData.bOAExpectations || [],
      ethnicMinorityPercentage: supplierData.ethnicMinorityPercentage,
      womenEmployeePercentage: supplierData.womenEmployeePercentage,
      executiveLeadershipRoles: supplierData.executiveLeadershipRoles || [],

      // section 2
      financingUsed: supplierData.financingUsed || [],
      costOfFinancing: supplierData.costOfFinancing,
      useForOutsideCapital: supplierData.useForOutsideCapital || [],
      currentRevenueRange: supplierData.currentRevenueRange,
      annualRevenue1: findYearlyTotalRevenue(dayjs().subtract(1, 'year').year()),
      annualRevenue2: findYearlyTotalRevenue(dayjs().subtract(2, 'year').year()),
      annualRevenue3: findYearlyTotalRevenue(dayjs().subtract(3, 'year').year()),

      // section 3
      digitalProducts: supplierData.digitalProducts,
      outsourcedServices: supplierData.outsourcedServices,

      // section 4
      capabilityStatementFileKey: supplierData.capabilitySheet?.fileKey,
      previousCustomers: supplierData.previousCustomers || [],
    },
    validate: {
      // section 1
      ownershipQuestion: FORM_VALIDATIONS.exists('Ownership question'),
      numberOfEmployees: FORM_VALIDATIONS.exists('number of employees'),
      numberOfContractors: FORM_VALIDATIONS.exists('number of contractors'),
      boardOfAdvisors: FORM_VALIDATIONS.noop,
      bOAExpectations: FORM_VALIDATIONS.list('Board Expectation', false, 3),

      ethnicMinorityPercentage: FORM_VALIDATIONS.exists('a percentage'),
      womenEmployeePercentage: FORM_VALIDATIONS.exists('a percentage'),
      executiveLeadershipRoles: FORM_VALIDATIONS.list(
        'Executive Leadership Roles',
        true,
        1
      ),

      // section 2
      financingUsed: FORM_VALIDATIONS.list('Funding Sources', true, 1),
      costOfFinancing: FORM_VALIDATIONS.basicText('financing cost', 3, true),
      useForOutsideCapital: FORM_VALIDATIONS.list(
        'Purpose of External Funding',
        true,
        1
      ),
      annualRevenue1: FORM_VALIDATIONS.exists('a number'),
      annualRevenue2: FORM_VALIDATIONS.exists('a number'),
      annualRevenue3: FORM_VALIDATIONS.exists('a number'),

      // section 3
      digitalProducts: (value: string[] | undefined) =>
        FORM_VALIDATIONS.list('Digital products', true, 3)(value ?? []),
      outsourcedServices: (value: string[] | undefined) =>
        FORM_VALIDATIONS.list('Outsourced Services', true, 3)(value ?? []),

      // section 4
      capabilityStatementFileKey: FORM_VALIDATIONS.noop,
      previousCustomers: FORM_VALIDATIONS.list('Previous Customers', false, 1),
    },
  });

  const industryForm = useForm({
    mode: 'controlled',
    validateInputOnChange: true,
    initialValues: {
      customerTypes: supplierData.customerTypes || [],
      top5Industries: supplierData.top5Industries || [],
      primarySalesChannels: supplierData.primarySalesChannels || [],
      currentCustomers: supplierData.currentCustomers || [],
    },
    validate: {
      customerTypes: FORM_VALIDATIONS.list('Primary Customer', true, 1),
      top5Industries: FORM_VALIDATIONS.list('Industry', true, 1),
      primarySalesChannels: FORM_VALIDATIONS.list('Revenue Channel', true, 1),
      currentCustomers: FORM_VALIDATIONS.list('Customer', false, 1),
    },
  });

  const updateSupplier = async (
    supplier: Supplier
  ): Promise<Supplier> => {
    return updateOrg(supplier).unwrap() as Promise<Supplier>;
  }

  const completeSupplierIntake = async (): Promise<void> => {
    return updateSupplierStatusFlagsMutation({
      hasCompletedSupplierIntake: true
    }).unwrap() as Promise<void>;
  }
  
  return (
    <SupplierIntakeContext.Provider
      value={{
        ...prefs,
        currentStep,
        setCurrentStep,
        lastStep,
        setLastStep,
        show,
        setShow,
        supplier,
        detailsForm,
        industryForm,
        structureForm,
        demographicCertifications,
        setDemographicCertifications,
        naics,
        setNAICS,
        primaryNAICS,
        setPrimaryNAICS,
        updateSupplier,
        completeSupplierIntake,
      }}
    >
      {children}
    </SupplierIntakeContext.Provider>
  );
};

export const useSupplierIntakeContext = () => useContext(SupplierIntakeContext);
